import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useEventTheme } from '../../context/ThemeContext';
import { DashboardLayout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowRight,
  Camera,
  CaretLeft,
  CaretRight,
  Clock,
  Gift,
  Heart,
  MapPinLine,
  NavigationArrow,
  Phone,
  Sparkle,
  TShirt,
  UsersThree,
} from '@phosphor-icons/react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';
import {
  GuestHeroSlideshow,
  guestService,
  LivePhotoWall,
  useGuests,
} from '../../features/guests';
import { colorWithAlpha, createMutedPalette } from '../../utils/mediaPalette';
import { getVenueLocationUrl } from '../../lib/locationLinks';
import { getInvitedItineraryEventIds } from './guestPartyUtils';

const getItinerarySideDetails = (side) => {
  if (side === 'bride') {
    return { label: 'Bride side', Icon: Heart, className: 'border-rose-200 bg-rose-50 text-rose-700' };
  }
  if (side === 'groom') {
    return { label: 'Groom side', Icon: UsersThree, className: 'border-sky-200 bg-sky-50 text-sky-700' };
  }
  return { label: 'Both sides', Icon: UsersThree, className: 'border-secondary/40 bg-secondary/10 text-foreground' };
};

export default function GuestDashboard() {
  const { currentEvent, user, effectiveRole } = useAuth();
  const { theme } = useEventTheme();
  const navigate = useNavigate();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const {
    guestProfile,
    loading,
    error: guestProfileError,
  } = useGuests(currentEvent?.id, user?.id);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [testimonialMessage, setTestimonialMessage] = useState('');
  const [savingTestimonial, setSavingTestimonial] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [activeWishIndex, setActiveWishIndex] = useState(0);
  const [invitedItineraryEventIds, setInvitedItineraryEventIds] = useState(null);

  const routeRole = effectiveRole || user?.role;
  const shouldFilterGuestItinerary = ['guest', 'admin_guest'].includes(routeRole);

  useEffect(() => {
    if (!currentEvent?.id || !shouldFilterGuestItinerary) {
      setInvitedItineraryEventIds(null);
      return undefined;
    }

    let active = true;
    guestService.getMyGuestGroup(currentEvent.id)
      .then((groupData) => {
        if (!active) return;
        setInvitedItineraryEventIds(getInvitedItineraryEventIds(groupData, groupData?.group || []));
        const namedGuests = (groupData?.group || []).filter((guest) => !(
          ['manager', 'rsvp'].includes(guest?.accompanying_guest_source)
          && guest?.accompanying_owner_guest_id
        ));
        if (namedGuests.length > 1 && !groupData?.primary_guest_id) {
          navigate('/guest/rsvp', { replace: true, state: { identitySelectionRequired: true } });
        }
      })
      .catch((error) => {
        console.error('Failed to check guest identity selection', error);
        if (active) setInvitedItineraryEventIds([]);
      });

    return () => {
      active = false;
    };
  }, [currentEvent?.id, navigate, shouldFilterGuestItinerary]);

  const resolveAssetUrl = (url) => (
    url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url
  );

  const sortedItinerary = useMemo(
    () => [...(currentEvent?.itinerary || [])]
      .filter((item) => (
        !shouldFilterGuestItinerary
        || (invitedItineraryEventIds || []).includes(item.id)
      ))
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [currentEvent?.itinerary, invitedItineraryEventIds, shouldFilterGuestItinerary]
  );

  const upcomingEvents = sortedItinerary.slice(0, 4);
  const eventDateRange = currentEvent?.end_date
    ? `${format(new Date(currentEvent.start_date), 'MMMM d')} - ${format(new Date(currentEvent.end_date), 'MMMM d, yyyy')}`
    : currentEvent?.start_date
      ? format(new Date(currentEvent.start_date), 'MMMM d, yyyy')
      : '';

  const hostNames = eventTypeConfig.isWedding
    ? [currentEvent?.bride_name, currentEvent?.groom_name].filter(Boolean).join(' & ')
    : eventTypeConfig.isBirthday
      ? (currentEvent?.honoree_names || []).join(' & ')
    : (currentEvent?.host_names || []).join(' & ');
  const eventDisplayName = hostNames || currentEvent?.name || eventTypeConfig.labels.guestHomeTitleFallback;
  const uploadedCoupleImage = resolveAssetUrl(
    currentEvent?.wedding_photos_preview?.[0]
  );
  const featuredEventPhoto = currentEvent?.guest_experience?.couple_photo_url || uploadedCoupleImage;
  const configuredHeroMedia = currentEvent?.guest_experience?.hero_media || [];
  const heroMedia = configuredHeroMedia.length
    ? configuredHeroMedia
    : (currentEvent?.wedding_photos_preview || []).map((url, index) => ({
      id: `legacy-hero-${index}`,
      media_type: 'image',
      url,
      sort_order: index,
      visible: true,
      duration_seconds: 6,
    }));
  const primaryColor = theme.colors.primary;
  const accentColor = theme.colors.secondary;
  const heroSurfacePalette = useMemo(
    () => createMutedPalette(primaryColor, accentColor),
    [accentColor, primaryColor]
  );
  const secondarySurfacePalette = useMemo(
    () => createMutedPalette(accentColor, primaryColor),
    [accentColor, primaryColor]
  );
  const featuredSurfacePalette = secondarySurfacePalette;
  const heroAccent = accentColor;
  const themePalette = useMemo(() => ({
    primary: primaryColor,
    accent: accentColor,
    rose: heroSurfacePalette.soft,
    sage: heroSurfacePalette.glow,
    gold: colorWithAlpha(accentColor, 0.24),
    cream: theme.colors.background,
    blushBase: heroSurfacePalette.soft,
    mistBase: heroSurfacePalette.glow,
    sectionBase: theme.colors.background,
  }), [accentColor, heroSurfacePalette, primaryColor, theme.colors.background]);
  const eventStory = currentEvent?.description || eventTypeConfig.labels.guestHomeStoryFallback;
  const highlightedVenue = upcomingEvents[0]?.venue || currentEvent?.venues?.[0];
  const displayVenue = highlightedVenue?.name || highlightedVenue?.address || '';
  const uppercaseDate = eventDateRange.toUpperCase();
  const desktopMenuItems = [
    { label: 'Home', href: '#top' },
    { label: 'Photo Wall', href: '#photo-wall' },
    { label: 'Itinerary', href: '#itinerary', featureKey: 'showItinerary' },
    { label: 'Wishes', href: '#wishes' },
    { label: 'Photos', to: '/guest/photos' },
    { label: 'Wishlist', to: '/guest/gifts' },
    { label: 'RSVP', to: '/guest/rsvp' },
  ].filter((item) => !item.featureKey || eventTypeConfig.features?.[item.featureKey]);

  const secondaryActions = [
    { to: '/guest/photos', icon: Camera, label: 'Photos', description: eventTypeConfig.isWedding ? 'Browse shared memories and wedding albums.' : eventTypeConfig.isBabyShower ? 'Browse baby shower memories and celebration albums.' : 'Browse shared memories and featured event albums.' },
    { to: '/guest/gifts', icon: Gift, label: eventTypeConfig.labels.registryShortTitle, description: eventTypeConfig.labels.registryDescription, featureKey: 'showWishlist' },
    { to: '/guest/wardrobe', icon: TShirt, label: 'Wardrobe', description: 'Review dress code ideas and outfit notes.', featureKey: 'showWardrobe' },
    { to: '/guest/contacts', icon: Phone, label: 'Contacts', description: 'Reach the right people if you need help.' },
  ].filter((action) => !action.featureKey || eventTypeConfig.features?.[action.featureKey]);

  const countdown = useMemo(() => {
    if (!currentEvent?.start_date) {
      return null;
    }

    const target = new Date(currentEvent.start_date);
    const diff = target.getTime() - now.getTime();
    const totalSeconds = Math.max(0, Math.floor(diff / 1000));

    return {
      isPast: diff <= 0,
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  }, [currentEvent?.start_date, now]);

  const rsvpTone = guestProfile?.rsvp_status === 'confirmed'
    ? 'bg-emerald-100 text-emerald-700'
    : guestProfile?.rsvp_status === 'declined'
      ? 'bg-rose-100 text-rose-700'
      : 'bg-amber-100 text-amber-700';

  useEffect(() => {
    if (guestProfileError) {
      console.error('Failed to fetch profile:', guestProfileError);
    }
  }, [guestProfileError]);

  useEffect(() => {
    if (!currentEvent?.id) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [currentEvent?.id]);

  useEffect(() => {
    if (testimonials.length <= 1) {
      setActiveWishIndex(0);
      return undefined;
    }

    const interval = window.setInterval(() => {
      setActiveWishIndex((current) => (current + 1) % testimonials.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [testimonials]);

  const fetchTestimonials = useCallback(async () => {
    if (!currentEvent?.id) return;

    try {
      setTestimonialsLoading(true);
      const nextTestimonials = await guestService.getTestimonials(currentEvent.id);
      setTestimonials(nextTestimonials);
      const myTestimonial = nextTestimonials.find((item) => item.guest_user_id === user?.id);
      if (myTestimonial?.message) {
        setTestimonialMessage(myTestimonial.message);
      }
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setTestimonialsLoading(false);
    }
  }, [currentEvent?.id, user?.id]);

  useEffect(() => {
    if (!currentEvent?.id) {
      return;
    }

    fetchTestimonials();
  }, [currentEvent?.id, fetchTestimonials]);

  const handleSaveTestimonial = async () => {
    const message = testimonialMessage.trim();
    if (message.length < 10) {
      toast.error('Please write at least 10 characters for your testimonial');
      return;
    }

    try {
      setSavingTestimonial(true);
      await guestService.saveTestimonial(currentEvent.id, message);
      toast.success('Your testimonial has been shared');
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      toast.error(error.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setSavingTestimonial(false);
    }
  };

  const activeWish = testimonials[activeWishIndex] || null;
  const hasMultipleWishes = testimonials.length > 1;

  const goToPreviousWish = () => {
    if (!hasMultipleWishes) {
      return;
    }

    setActiveWishIndex((current) => (current - 1 + testimonials.length) % testimonials.length);
  };

  const goToNextWish = () => {
    if (!hasMultipleWishes) {
      return;
    }

    setActiveWishIndex((current) => (current + 1) % testimonials.length);
  };

  if (!currentEvent) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <img src="/assets/logo.png" alt="GalaGo logo" className="w-16 h-16 object-contain mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-bold text-wedding-charcoal mb-4">Welcome to GalaGo</h1>
          <p className="text-muted-foreground mb-8">
            You haven't joined any event yet. Use an invite code during registration to join an event.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-pulse">
          <div className="h-[360px] rounded-[2rem] bg-white/50" />
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="h-64 rounded-[2rem] bg-white/50" />
            <div className="h-64 rounded-[2rem] bg-white/50" />
          </div>
          <div className="h-96 rounded-[2rem] bg-white/50" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <section
          id="top"
          className="-mx-4 relative isolate overflow-hidden border-b border-white/20 transition-colors duration-700 md:-mx-6 lg:-mx-8"
          style={{
            backgroundColor: themePalette.cream,
            backgroundImage: `
              radial-gradient(circle at 88% 13%, ${colorWithAlpha(themePalette.accent, 0.34)} 0%, transparent 34%),
              radial-gradient(circle at 8% 58%, ${colorWithAlpha(themePalette.primary, 0.3)} 0%, transparent 38%),
              linear-gradient(180deg, ${heroSurfacePalette.deep} 0%, ${secondarySurfacePalette.deep} 66%, ${themePalette.sectionBase} 66%, ${themePalette.cream} 100%)
            `,
          }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              className="absolute -left-16 top-8 h-48 w-48 rounded-full blur-3xl"
              style={{ backgroundColor: colorWithAlpha(featuredSurfacePalette.soft, 0.12) }}
            />
            <div
              className="absolute -right-12 top-36 h-56 w-56 rounded-full blur-3xl"
              style={{ backgroundColor: colorWithAlpha(heroSurfacePalette.glow, 0.22) }}
            />
            <div className="absolute bottom-0 left-1/2 h-28 w-[140%] -translate-x-1/2 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent)]" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 pb-8 pt-2 text-center text-white md:px-8 md:pt-2">
            <div className="hidden md:block">
              <div
                className="flex items-center justify-between border-y py-2 text-[0.75rem] uppercase tracking-[0.26em] backdrop-blur-sm"
                style={{
                  borderColor: colorWithAlpha(heroAccent, 0.56),
                  color: 'rgba(255,255,255,0.92)',
                  textShadow: '0 1px 12px rgba(20, 11, 14, 0.28)',
                }}
              >
                {desktopMenuItems.map((item) => (
                  item.to ? (
                    <Link key={item.label} to={item.to} className="transition-opacity hover:opacity-100 opacity-80">
                      {item.label}
                    </Link>
                  ) : (
                    <a key={item.label} href={item.href} className="transition-opacity hover:opacity-100 opacity-80">
                      {item.label}
                    </a>
                  )
                ))}
              </div>
            </div>

            <div className="relative px-2 pb-2 pt-3 md:px-0">
              <div className="grid gap-2 md:grid-cols-[1fr_minmax(0,2fr)_1fr] md:items-center">
                <p
                  className="relative inline-flex items-center justify-self-center rounded-full border px-3 py-1 text-[0.625rem] uppercase tracking-[0.36em] text-white/85 backdrop-blur-md md:justify-self-start"
                  style={{
                    backgroundColor: colorWithAlpha(featuredSurfacePalette.soft, 0.1),
                    borderColor: colorWithAlpha(heroAccent, 0.32),
                  }}
                >
                  {guestProfile ? `Welcome ${guestProfile.name}` : eventTypeConfig.labels.guestHomeBadge}
                </p>
                <h1
                  className="relative mt-2 font-serif text-[1.8rem] uppercase tracking-[0.14em] md:mt-0 md:text-[2.55rem]"
                  data-testid="guest-event-name"
                  style={{ textShadow: '0 3px 30px rgba(18, 10, 13, 0.32)' }}
                >
                  {eventDisplayName}
                </h1>
                <div className="mt-1 md:mt-0 md:text-right">
                  <p className="text-[0.75rem] uppercase tracking-[0.3em] text-white/90 md:text-[0.8125rem]">
                    {uppercaseDate}
                  </p>
                  {displayVenue && (
                    <p className="mt-1 text-[0.625rem] uppercase tracking-[0.24em] text-white/75 md:text-[0.6875rem]">
                      {displayVenue}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 md:mt-2">
                <GuestHeroSlideshow
                  media={heroMedia}
                  featuredPhotoUrl={featuredEventPhoto}
                  displayNames={eventDisplayName}
                  placeholderUrl={eventTypeConfig.isWedding ? '/assets/guest-couple-placeholder.png' : '/assets/logo.png'}
                  accentColor={heroAccent}
                  surfaceColor={heroSurfacePalette.deep}
                />
              </div>

              <div className="mx-auto mt-2 max-w-4xl overflow-hidden rounded-[2rem] border border-border bg-card/95 text-left text-wedding-charcoal shadow-[0_22px_55px_rgba(0,0,0,0.12)] backdrop-blur-xl">
                <div
                  className="h-1 w-full"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${colorWithAlpha(heroAccent, 0.9)}, transparent)`,
                  }}
                />
                <div className="grid md:grid-cols-[minmax(0,1fr)_19rem] md:items-stretch">
                  <div className="px-6 py-6 md:px-8 md:py-7">
                    <div className="flex items-center gap-3">
                      <span
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                        style={{
                          backgroundColor: colorWithAlpha(heroAccent, 0.14),
                          color: heroSurfacePalette.deep,
                        }}
                      >
                        <Sparkle className="h-4 w-4" weight="fill" />
                      </span>
                      <p className="text-[0.625rem] font-medium uppercase tracking-[0.34em] text-wedding-charcoal/50">
                        A note from your hosts
                      </p>
                    </div>
                    <p className="mt-4 font-serif text-xl leading-8 text-wedding-charcoal/85 md:text-2xl md:leading-9">
                      {eventStory}
                    </p>
                  </div>

                  <div className="border-t border-border bg-background/40 px-6 py-5 md:flex md:flex-col md:justify-center md:border-l md:border-t-0 md:px-7">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-[0.625rem] font-medium uppercase tracking-[0.3em] text-wedding-charcoal/45">
                          Your response
                        </p>
                        <Badge className={`mt-2 border-0 capitalize shadow-none ${rsvpTone}`}>
                          {guestProfile?.rsvp_status || 'pending'}
                        </Badge>
                      </div>
                      <span
                        className="h-10 w-px"
                        style={{ backgroundColor: colorWithAlpha(heroAccent, 0.28) }}
                      />
                      <p className="max-w-[8rem] text-right text-xs leading-5 text-wedding-charcoal/55">
                        Let your hosts know if you can join.
                      </p>
                    </div>
                    <Link to="/guest/rsvp" className="mt-4 block">
                      <Button
                        className="h-12 w-full rounded-full border border-secondary bg-primary px-6 font-medium uppercase tracking-[0.22em] text-primary-foreground shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-primary/90"
                        data-testid="update-rsvp-btn"
                      >
                        Update RSVP
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section
          className="-mx-4 border-y border-white/40 animate-in fade-in-0 slide-in-from-bottom-2 duration-700 md:-mx-6 lg:-mx-8"
          style={{
            background: `linear-gradient(90deg, ${themePalette.rose} 0%, ${themePalette.sage} 100%)`,
          }}
        >
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-5 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <p className="inline-flex rounded-full border border-white/40 bg-white/65 px-3 py-1 text-xs uppercase tracking-[0.32em] text-wedding-charcoal/65">
                {eventTypeConfig.labels.countdownLabel}
              </p>
              <h2 className="mt-2 font-serif text-2xl text-wedding-charcoal md:text-3xl">
                {countdown?.isPast ? 'The event has begun' : 'A little time left before the event begins'}
              </h2>
            </div>
            <div className="grid grid-cols-4 gap-3 md:min-w-[420px]">
              {[
                { label: 'Days', value: countdown?.days ?? 0 },
                { label: 'Hours', value: countdown?.hours ?? 0 },
                { label: 'Minutes', value: countdown?.minutes ?? 0 },
                { label: 'Seconds', value: countdown?.seconds ?? 0 },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/70 bg-[rgba(255,255,255,0.82)] px-3 py-4 text-center shadow-sm backdrop-blur animate-in zoom-in-95 duration-500">
                  <p className="font-serif text-3xl text-wedding-charcoal md:text-4xl">{item.value}</p>
                  <p className="mt-1 text-[0.6875rem] uppercase tracking-[0.26em] text-wedding-charcoal/55">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <LivePhotoWall event={currentEvent} />

        {eventTypeConfig.features.showItinerary && <section
          id="itinerary"
          className="-mx-4 relative isolate overflow-hidden border-y animate-in fade-in-0 slide-in-from-bottom-2 duration-700 md:-mx-6 lg:-mx-8"
          style={{
            background: `linear-gradient(145deg, ${themePalette.cream} 0%, ${themePalette.cream} 58%, ${themePalette.gold} 150%)`,
            borderColor: colorWithAlpha(themePalette.accent, 0.48),
          }}
        >
          <div
            className="h-2 w-full"
            style={{
              background: `repeating-linear-gradient(135deg, ${heroSurfacePalette.deep} 0 8px, ${themePalette.accent} 8px 12px, ${heroSurfacePalette.deep} 12px 20px)`,
            }}
          />
          <div
            className="relative overflow-hidden px-5 py-6 text-white md:px-10 md:py-10"
            style={{
              background: `linear-gradient(125deg, ${heroSurfacePalette.deep} 0%, ${themePalette.primary} 42%, ${secondarySurfacePalette.deep} 100%)`,
            }}
          >
            <div className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full border border-secondary/20" />
            <div className="pointer-events-none absolute -right-12 -top-16 h-52 w-52 rounded-full border border-secondary/25" />
            <div className="pointer-events-none absolute -right-2 top-0 h-32 w-32 rotate-45 border border-secondary/15" />
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage: `linear-gradient(45deg, ${colorWithAlpha(themePalette.accent, 0.8)} 1px, transparent 1px), linear-gradient(-45deg, ${colorWithAlpha(themePalette.accent, 0.8)} 1px, transparent 1px)`,
                backgroundSize: '28px 28px',
              }}
            />
            <div className="relative flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="h-px w-10 bg-secondary" />
                <Sparkle className="h-3.5 w-3.5 text-secondary" weight="fill" />
                <p className="text-[0.625rem] font-medium uppercase tracking-[0.38em] text-white/80">
                  {eventTypeConfig.labels.itineraryEyebrow}
                </p>
                <span className="hidden h-px w-10 bg-secondary sm:block" />
              </div>
              <h2 className="mt-3 font-serif text-2xl leading-tight text-white md:mt-4 md:text-[2.65rem]">
                {eventTypeConfig.labels.itineraryTitle}
              </h2>
            </div>
            <Link to="/guest/itinerary">
              <Button
                variant="outline"
                className="h-9 rounded-full border-secondary/70 bg-white/10 px-4 text-xs text-white shadow-sm backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/20 hover:text-white md:h-11 md:px-5 md:text-sm"
              >
                View full itinerary
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            </div>
            <div className="absolute bottom-0 left-1/2 flex -translate-x-1/2 items-center">
              <span className="h-px w-20 bg-gradient-to-r from-transparent to-secondary md:w-32" />
              <span className="h-2.5 w-2.5 rotate-45 border border-secondary bg-primary" />
              <span className="h-px w-20 bg-gradient-to-l from-transparent to-secondary md:w-32" />
            </div>
          </div>

          {upcomingEvents.length > 0 ? (
            <div
              className="relative p-2 md:p-6"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, ${colorWithAlpha(themePalette.accent, 0.13)} 1px, transparent 0)`,
                backgroundSize: '22px 22px',
              }}
            >
              <div className="overflow-hidden rounded-[1.25rem] border border-secondary/30 bg-card/90 shadow-[0_18px_50px_rgba(0,0,0,0.08)] backdrop-blur-sm md:rounded-[1.75rem]">
                {upcomingEvents.map((event, index) => (
                  <div
                    key={event.id}
                    className={`group relative grid grid-cols-[5.25rem_minmax(0,1fr)] items-center gap-3 px-3 py-4 transition-colors hover:bg-white/65 md:grid-cols-[8.5rem_minmax(0,1fr)_auto] md:gap-5 md:px-7 md:py-6 ${
                      index > 0 ? 'border-t border-secondary/25' : ''
                    }`}
                  >
                    <div
                      className="relative flex h-full w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-b-xl rounded-t-[2rem] border px-2 py-3 shadow-[inset_0_0_0_3px_rgba(255,255,255,0.45)] md:h-auto md:w-auto md:flex-row md:gap-4 md:rounded-b-2xl md:rounded-t-[2.75rem] md:px-4 md:py-4"
                      style={{
                        background: `linear-gradient(145deg, ${themePalette.rose} 0%, ${themePalette.cream} 100%)`,
                        borderColor: colorWithAlpha(themePalette.accent, 0.5),
                      }}
                    >
                      <span className="absolute left-1/2 top-1.5 h-1 w-1 -translate-x-1/2 rotate-45 bg-secondary" />
                      <span className="font-serif text-3xl leading-none text-wedding-charcoal md:text-4xl">
                        {format(new Date(event.date), 'd')}
                      </span>
                      <span className="flex flex-col items-center border-secondary/25 md:items-start md:border-l md:pl-4">
                        <span className="text-[0.625rem] font-medium uppercase tracking-[0.3em] text-primary">
                          {format(new Date(event.date), 'MMM')}
                        </span>
                        <span className="mt-0.5 text-[0.625rem] font-medium text-wedding-charcoal/75 md:mt-1 md:text-xs">
                          {format(new Date(event.date), 'EEEE')}
                        </span>
                      </span>
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-serif text-xl leading-tight text-wedding-charcoal md:text-[1.7rem]">{event.name}</h3>
                        {eventTypeConfig.isWedding && (() => {
                          const sideDetails = getItinerarySideDetails(event.side);
                          const SideIcon = sideDetails.Icon;
                          return (
                            <Badge
                              variant="outline"
                              className={`rounded-full px-3 ${sideDetails.className}`}
                              data-testid={`guest-itinerary-side-${event.id}`}
                            >
                              <SideIcon className="mr-1 h-3 w-3" />
                              {sideDetails.label}
                            </Badge>
                          );
                        })()}
                        {event.dress_code && (
                          <Badge
                            variant="outline"
                            className="rounded-full border-secondary/50 bg-secondary/15 px-3 text-foreground"
                          >
                            {event.dress_code}
                          </Badge>
                        )}
                      </div>
                      {event.description && (
                        <p className="mt-2 hidden max-w-3xl text-sm leading-6 text-wedding-charcoal/60 sm:block">{event.description}</p>
                      )}
                      <div className="mt-2 flex flex-col gap-1.5 text-xs text-wedding-charcoal/60 sm:flex-row sm:flex-wrap sm:gap-x-5 sm:gap-y-2 md:mt-3 md:text-sm">
                        <span className="inline-flex items-center gap-2">
                          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-secondary/20 text-primary md:h-7 md:w-7">
                            <Clock className="h-3.5 w-3.5" />
                          </span>
                          {event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}
                        </span>
                        {event.venue?.name && (
                          getVenueLocationUrl(event.venue) ? (
                            <a
                              href={getVenueLocationUrl(event.venue)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 font-semibold text-primary hover:underline"
                              aria-label={`Open ${event.venue.name} in Google Maps`}
                            >
                              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-secondary/20 text-primary md:h-7 md:w-7">
                                <NavigationArrow className="h-3.5 w-3.5" weight="fill" />
                              </span>
                              <span>{event.venue.name}{event.venue.hall_name ? `, ${event.venue.hall_name}` : ''}</span>
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-secondary/20 text-primary md:h-7 md:w-7">
                                <MapPinLine className="h-3.5 w-3.5" />
                              </span>
                              <span>{event.venue.name}{event.venue.hall_name ? `, ${event.venue.hall_name}` : ''}</span>
                            </span>
                          )
                        )}
                      </div>
                    </div>

                    <div className="hidden min-w-[5rem] text-right md:block">
                      <p className="text-[0.5625rem] font-medium uppercase tracking-[0.3em] text-primary">
                        Celebration
                      </p>
                      <p className="mt-1 font-serif text-2xl text-wedding-charcoal/70">
                        {String(index + 1).padStart(2, '0')}
                      </p>
                    </div>
                  </div>
                ))}
                {sortedItinerary.length > 2 && (
                  <div className="border-t border-secondary/25 bg-background/45 px-4 py-4 text-center md:px-7 md:py-5">
                    <Button asChild className="h-11 rounded-full px-6">
                      <Link to="/guest/itinerary" data-testid="view-full-itinerary-bottom">
                        View full itinerary
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="m-4 rounded-[1.5rem] border border-dashed border-border bg-card/55 p-10 text-center text-wedding-charcoal/65 md:m-6">
              {shouldFilterGuestItinerary
                ? invitedItineraryEventIds === null
                  ? 'Loading your itinerary...'
                  : 'No itinerary events are assigned to your invitation.'
                : 'The detailed event timeline will appear here once it is published.'}
            </div>
          )}
        </section>}

        <section
          id="wishes"
          className="-mx-4 relative isolate overflow-hidden py-7 animate-in fade-in-0 slide-in-from-bottom-2 duration-700 md:-mx-6 md:py-14 lg:-mx-8"
          style={{ background: `linear-gradient(135deg, ${themePalette.blushBase} 0%, ${themePalette.cream} 48%, ${themePalette.rose} 140%)` }}
        >
          <div className="pointer-events-none absolute -left-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full border border-secondary/10" />
          <div className="pointer-events-none absolute -left-12 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full border border-secondary/15" />
          <div className="relative mx-auto w-full max-w-7xl px-4 md:px-8 lg:px-10">
            <div className="mx-auto mb-5 max-w-2xl text-center md:mb-8">
              <div className="flex items-center justify-center gap-3 text-secondary">
                <span className="h-px w-12 bg-current/45" />
                <Sparkle className="h-4 w-4" weight="fill" />
                <span className="h-px w-12 bg-current/45" />
              </div>
              <p className="mt-3 text-[0.625rem] font-medium uppercase tracking-[0.38em] text-wedding-charcoal/45">
                Notes from our favourite people
              </p>
              <h2 className="mt-2 font-serif text-2xl text-wedding-charcoal md:mt-3 md:text-4xl">
                Wishes to keep forever
              </h2>
            </div>

            <div className="grid gap-4 md:gap-6 lg:grid-cols-[1.08fr_0.92fr] lg:items-stretch">
              <div>
                <div className="h-full">
                  {testimonialsLoading ? (
                    <div className="grid h-full gap-3 rounded-[1.75rem] border border-border bg-card p-7 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
                      {[1, 2].map((item) => (
                        <div key={item} className="px-2 py-3 animate-pulse md:px-4">
                          <div className="h-4 w-32 rounded bg-muted" />
                          <div className="mt-3 h-4 w-full rounded bg-muted" />
                          <div className="mt-2 h-4 w-4/5 rounded bg-muted" />
                        </div>
                      ))}
                    </div>
                  ) : activeWish ? (
                    <div
                      className="relative flex h-full min-h-[16rem] flex-col justify-between overflow-hidden rounded-[0.4rem] border border-border bg-card px-5 py-5 shadow-[0_22px_60px_rgba(0,0,0,0.1)] md:min-h-[22rem] md:px-10 md:py-9"
                    >
                      <span className="pointer-events-none absolute -right-2 -top-10 font-serif text-[8rem] leading-none text-secondary/10 md:-right-3 md:-top-14 md:text-[11rem]">
                        &ldquo;
                      </span>
                      <div className="relative animate-in fade-in-0 zoom-in-95 duration-500">
                        <p className="text-[0.625rem] uppercase tracking-[0.34em] text-primary">A cherished note</p>
                        <p className="mt-4 font-serif text-xl leading-7 text-wedding-charcoal/85 md:mt-6 md:text-3xl md:leading-[2.8rem]">
                          &ldquo;{activeWish.message}&rdquo;
                        </p>
                      </div>
                      <div className="relative mt-5 flex flex-wrap items-end justify-between gap-3 border-t border-border pt-4 md:mt-8 md:gap-4 md:pt-5">
                        <div>
                          <p className="font-serif text-lg text-wedding-charcoal">{activeWish.guest_name}</p>
                          <p className="mt-1 text-[0.625rem] uppercase tracking-[0.24em] text-wedding-charcoal/40">
                            {activeWish.created_at ? format(new Date(activeWish.created_at), 'MMM d, yyyy') : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="hidden gap-1.5 sm:flex">
                            {testimonials.map((item, index) => (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => setActiveWishIndex(index)}
                                className={`h-1.5 rounded-full transition-all ${index === activeWishIndex ? 'w-7 bg-primary' : 'w-1.5 bg-muted'}`}
                                aria-label={`View wish ${index + 1}`}
                              />
                            ))}
                          </div>
                          {hasMultipleWishes && (
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={goToPreviousWish}
                                className="h-9 w-9 rounded-full border-border bg-transparent text-wedding-charcoal hover:bg-muted"
                                aria-label="View previous wish"
                              >
                                <CaretLeft className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={goToNextWish}
                                className="h-9 w-9 rounded-full border-border bg-transparent text-wedding-charcoal hover:bg-muted"
                                aria-label="View next wish"
                              >
                                <CaretRight className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[18rem] items-center justify-center rounded-[0.4rem] border border-dashed border-border bg-card px-8 text-center text-sm text-wedding-charcoal/60">
                      No messages yet. Be the first guest to leave a lovely note.
                    </div>
                  )}
                </div>
              </div>
              <div
                className="relative overflow-hidden rounded-[1.5rem] p-5 text-white shadow-[0_22px_60px_rgba(0,0,0,0.18)] md:rounded-[1.75rem] md:p-8"
                style={{ background: `linear-gradient(145deg, ${themePalette.primary} 0%, ${secondarySurfacePalette.deep} 100%)` }}
              >
                <div className="pointer-events-none absolute -bottom-20 -right-20 h-56 w-56 rounded-full border border-white/10" />
                <p className="relative text-[0.625rem] font-medium uppercase tracking-[0.34em] text-white/75">
                  Add to the keepsake
                </p>
                <h3 className="relative mt-3 font-serif text-2xl">Leave your wish</h3>
                <div className="relative mt-4 space-y-3 md:mt-6 md:space-y-4">
                  <Textarea
                    value={testimonialMessage}
                    onChange={(e) => setTestimonialMessage(e.target.value)}
                    placeholder="Write a sweet message, a memory, or what you're most looking forward to..."
                    className="min-h-[105px] resize-none rounded-[1.1rem] border-white/15 bg-white/10 px-4 py-3 text-sm leading-6 text-white placeholder:text-white/35 focus-visible:ring-secondary md:min-h-[145px] md:rounded-[1.25rem]"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[0.625rem] uppercase tracking-[0.22em] text-white/40">
                      {testimonialMessage.trim().length} characters
                    </p>
                    <Button
                      type="button"
                      onClick={handleSaveTestimonial}
                      disabled={savingTestimonial}
                      className="rounded-full bg-secondary px-6 text-secondary-foreground shadow-lg hover:bg-secondary/90 focus-visible:ring-secondary"
                      data-testid="share-wish-btn"
                    >
                      {savingTestimonial ? 'Saving...' : 'Share Wish'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="-mx-4 relative overflow-hidden px-4 py-7 animate-in fade-in-0 slide-in-from-bottom-2 duration-700 md:-mx-6 md:px-8 md:py-12 lg:-mx-8 lg:px-10"
          style={{ background: `linear-gradient(135deg, ${themePalette.mistBase} 0%, ${themePalette.cream} 60%, ${themePalette.sage} 150%)` }}
        >
          <div
            className="absolute inset-y-0 left-0 w-1.5"
            style={{ background: `linear-gradient(180deg, ${themePalette.primary}, ${themePalette.accent})` }}
          />
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[0.625rem] font-medium uppercase tracking-[0.34em] text-wedding-charcoal/45">
                Guest concierge
              </p>
              <h2 className="mt-2 font-serif text-2xl text-wedding-charcoal md:text-3xl">Everything for the celebration</h2>
            </div>
            <p className="hidden max-w-md text-sm leading-6 text-wedding-charcoal/55 sm:block md:text-right">
              Photos, gifts, attire and people to call—all thoughtfully gathered in one place.
            </p>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2.5 md:mt-7 md:gap-3 xl:grid-cols-4">
            {secondaryActions.map((action, index) => {
              const actionColor = index % 2 === 0 ? themePalette.primary : themePalette.accent;
              return (
                <Link
                  key={action.to}
                  to={action.to}
                  className="group relative flex min-h-[9rem] flex-col justify-between overflow-hidden rounded-[1.15rem] border border-border bg-card/70 p-3.5 shadow-[0_12px_35px_rgba(0,0,0,0.05)] backdrop-blur transition-all hover:-translate-y-1 hover:bg-card hover:shadow-[0_18px_40px_rgba(0,0,0,0.1)] md:min-h-[12rem] md:rounded-[1.4rem] md:p-5"
                  style={{ borderTopColor: actionColor, borderTopWidth: '3px' }}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-xl md:h-12 md:w-12 md:rounded-2xl"
                      style={{ backgroundColor: colorWithAlpha(actionColor, 0.14) }}
                    >
                      <action.icon className="h-5 w-5 md:h-6 md:w-6" style={{ color: actionColor }} />
                    </div>
                    <span className="font-serif text-sm text-wedding-charcoal/25">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="mt-3 md:mt-5">
                    <p className="font-serif text-base text-wedding-charcoal md:text-xl">{action.label}</p>
                    <p className="mt-1 hidden text-sm leading-5 text-wedding-charcoal/55 md:block">{action.description}</p>
                    <span
                      className="mt-2 inline-flex items-center text-[0.5625rem] font-medium uppercase tracking-[0.18em] text-wedding-charcoal/45 transition-colors md:mt-4 md:text-[0.625rem] md:tracking-[0.22em]"
                      style={{ color: actionColor }}
                    >
                      Explore
                      <ArrowRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

